import React from 'react'
import './RedactionTool.scss'
import { Portal } from 'react-portal'
import useCallbackState from './hooks/useCallbackState'
import showRedactionStyles from './showRedactionStyles.js'
import RedactionCanvas from './RedactionCanvas'
import PopupToolbar from './PopupToolbar'
import shortid from 'shortid'
import cheerio from 'cheerio'
import { /*BASE_URL,*/ GET_IMAGE_HTML, SAVE_HTML_REDACTION } from '../../requests'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { Spinner } from '../Loaders/Loaders'

//const CHARACTER_WIDTH = 12.33333;

const forEachRedactableImage = (callback) => {
  for (const image of document.querySelectorAll('[data-redactable-img="true"]')) {
    callback(image)
  }
}

const RedactionTool = ({ ...props }) => {
  const bodyFrame = React.useRef()
  const styleFrame = React.useRef()
  const selected = React.useRef()
  const virtualContainer = React.useRef()
  const [virtualText, setVirtualText] = useCallbackState({
    text: '',
    fontSize: 18,
  })
  const [showRedactable, setShowRedactable] = React.useState(false)
  const [redactingImageId, setRedactingImageId] = React.useState(null)
  const [redactingImage, setRedactingImage] = React.useState(null)
  const [isRedactingImage, setIsRedactingImage] = React.useState(false)
  const [originalImages, setOriginalImages] = React.useState({})
  const [popupOrigin, setPopupOrigin] = React.useState(null)
  const [textUndos, setTextUndos] = React.useState([])
  const htmlBody = React.useRef([])
  const htmlStyles = React.useRef([])
  const [htmlId, setHtmlId] = React.useState('')
  const currentHTML = React.useRef('')
  const params = useParams()
  const [loading, setLoading] = React.useState(true)

  const exportHTML = () => {
    setLoading(true)
    const hBody = bodyFrame.current.innerHTML
    const $ = cheerio.load(currentHTML.current)
    $('body').html(hBody)
    const html = $.root().html()
    const update = {
      imageID: params.imageNumber,
      htmlRedaction: html,
    }
    SAVE_HTML_REDACTION([update], () => {
      setLoading(false)
      window.close()
    })
  }

  window.setHTML = (html) => {
    const $ = cheerio.load(html)
    const hBody = [`<body>${$('body').html()}</body>`]
    const hStyles = $('style')
      .map(function () {
        return $(this).html()
      })
      .get()
      .map((s) => `<style>${s}</style>`)
    currentHTML.current = html
    htmlBody.current = hBody || []
    htmlStyles.current = hStyles || []
    setHtmlId(shortid.generate())
    setLoading(false)
  }

  const cacheSelection = () => {
    const currentSelection = document.getSelection()

    if (currentSelection.anchorNode !== currentSelection.focusNode) {
      const newRange = document.createRange()

      const endsInDifferentNode = currentSelection.anchorNode !== currentSelection.focusNode

      const endComesBeforeStart =
        endsInDifferentNode &&
        currentSelection.anchorNode.compareDocumentPosition(currentSelection.focusNode) === 2

      if (endComesBeforeStart) {
        newRange.setStart(currentSelection.anchorNode, 0)
        newRange.setEnd(currentSelection.anchorNode, 0)
      } else {
        newRange.setStartBefore(currentSelection.anchorNode)
        newRange.setEndAfter(currentSelection.anchorNode)
      }
      currentSelection.removeAllRanges()
      currentSelection.addRange(newRange)
      selected.current = currentSelection
    } else {
      selected.current = document.getSelection()
    }
    if (currentSelection.type === 'Range') {
      const rect = currentSelection.getRangeAt(0).getClientRects()[0]
      if (rect) {
        setPopupOrigin({
          x: rect.x + rect.width / 2 - 40,
          y: rect.y - 44,
        })
      }
    } else {
      setPopupOrigin(null)
    }
  }

  const handleImageClicked = (e) => {
    setRedactingImageId(e.target.dataset.imageId)
    setRedactingImage(e.target.src)
    setIsRedactingImage(true)
  }

  const redactSelectedText = () => {
    if (selected.current && selected.current.type !== 'None') {
      if (selected.current.rangeCount) {
        const range = selected.current.getRangeAt(0)
        const selectedText = range.extractContents().textContent
        const fontSize = window
          .getComputedStyle(
            range.startContainer.nodeType === 3
              ? range.startContainer.parentNode
              : range.startContainer,
            null
          )
          .getPropertyValue('font-size')

        setVirtualText({ text: selectedText, fontSize }, () => {
          // const containerWidth = virtualContainer.current.getBoundingClientRect()
          //   .width;
          const redactionText = new Array(selectedText.length).fill('█').join('')
          const redactionId = shortid.generate()
          setTextUndos((undos) => [
            ...undos,
            {
              id: redactionId,
              redactionText,
              selectedText,
              fontSize,
              startOffset: range.startOffset,
              endOffset: range.endOffset,
            },
          ])
          const outerDiv = document.createElement('span')
          outerDiv.style.display = 'inline'
          outerDiv.style.position = 'relative'
          outerDiv.className = 'redaction-block'
          outerDiv.dataset.redactionId = redactionId
          const redactionBars = document.createElement('span')
          redactionBars.setAttribute('aria-hidden', 'true')
          redactionBars.textContent = redactionText
          outerDiv.appendChild(redactionBars)
          range.deleteContents()
          range.insertNode(outerDiv)
          selected.current.empty()

          const allyText = document.createElement('span')
          allyText.textContent = 'redacted'
          allyText.className = 'redaction-ally-text'
          allyText.style.position = 'absolute'
          allyText.style.right = '1px'
          allyText.style.bottom = '3px'
          allyText.style.color = 'black'
          allyText.opacity = '.01'
          allyText.style.fontSize = '2px'
          allyText.ariaHidden = 'false'
          outerDiv.appendChild(allyText)
        })
      }
    }
  }

  const undoTextRedaction = () => {
    const lastEdit = textUndos[textUndos.length - 1]
    const newUndos = textUndos.slice(0, textUndos.length - 1)
    const redactionSpan = document.querySelector(`[data-redaction-id="${lastEdit.id}"]`)
    if (redactionSpan) {
      const redactionParent = redactionSpan.parentNode
      redactionSpan.remove()
      const parentHTML = redactionParent.innerHTML
      redactionParent.innerHTML = `${parentHTML.slice(0, lastEdit.startOffset)}${
        lastEdit.selectedText
      }${parentHTML.slice(lastEdit.startOffset)}`
      setTextUndos(newUndos)
    }
  }

  // componentDidMount
  React.useEffect(() => {
    document.addEventListener('selectionchange', cacheSelection)
    let original = {}
    forEachRedactableImage((img) => {
      const id = shortid.generate()
      img.dataset.imageId = id
      original[id] = img.src
      img.addEventListener('click', handleImageClicked)
    })
    setOriginalImages(original)
    return () => {
      document.removeEventListener('selectionchange', cacheSelection)
      forEachRedactableImage((img) => {
        img.removeEventListener('click', handleImageClicked)
      })
    }
  }, [htmlId])

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      GET_IMAGE_HTML(params.imageNumber, (htmlReturn) => {
        window.setHTML(htmlReturn)
      })
    }
    if (params.imageNumber) {
      GET_IMAGE_HTML(params.imageNumber, (htmlReturn) => {
        window.setHTML(htmlReturn)
      })
    }
  }, [])

  return (
    <div className="RedactionTool" data-is-redacting={isRedactingImage}>
      {loading ? (
        <div className="wvm-loader-wrapper">
          <Spinner className="page-loader" themeColor="dark-neutral" />
        </div>
      ) : (
        <React.Fragment>
          <PopupToolbar origin={popupOrigin} onRedactRequested={redactSelectedText} />
          <div className="toolbar">
            <div className="flex-center">
              <label
                className={`butt-box ${showRedactable ? 'checked' : ''}`}
                htmlFor="show-redactable"
              >
                <input
                  checked={showRedactable}
                  type="checkbox"
                  id="show-redactable"
                  onChange={(e) => setShowRedactable(e.target.checked)}
                />
                <label htmlFor="show-redactable">Show redactable fields</label>
              </label>
              <button onClick={undoTextRedaction} disabled={!textUndos.length}>
                Undo
              </button>
              <button onClick={exportHTML}>Save</button>
            </div>
          </div>
          {showRedactable ? <style type="text/css">{showRedactionStyles}</style> : null}
          {isRedactingImage ? <style type="text/css">{`body{overflow: hidden;}`}</style> : null}
          <div
            id="style-frame"
            ref={styleFrame}
            dangerouslySetInnerHTML={{ __html: htmlStyles.current.join('\n') }}
          />
          <div
            id="body-frame"
            ref={bodyFrame}
            dangerouslySetInnerHTML={{ __html: htmlBody.current[0] }}
          />
          <Portal>
            <div
              id="virtual-container"
              style={{ fontSize: virtualText.fontSize }}
              ref={virtualContainer}
            >
              {virtualText.text}
            </div>
          </Portal>
          <RedactionCanvas
            isOpen={isRedactingImage}
            image={redactingImage}
            onSaved={(newSrc) => {
              document.querySelector(`[data-image-id="${redactingImageId}"]`).src = newSrc
            }}
            onExited={() => setIsRedactingImage(false)}
            onOriginalImageRequested={() => {
              if (originalImages[redactingImageId]) {
                setRedactingImage(originalImages[redactingImageId])
              }
            }}
          />
        </React.Fragment>
      )}
    </div>
  )
}

export default RedactionTool
